import { useSearchParams } from 'react-router-dom';
import React from 'react';
import { useLivingBundleEntitlement, usePlusRentEntitlement } from './useGetHasProduct.hook';
import { queryClient } from '../components/Root';
import { BenefitsListPage } from '../@types/benefitsListPage';
import { useProducts } from './useProducts';
import { getShouldRefetchProductsAndEntitlements } from '../utils/getShouldRefetchProductsAndEntitlements.util';
import useNavigationPaths from './useNavigationPaths.hook';

enum RefetchStatus {
  DISABLED = 'DISABLED',
  REFETCHING = 'REFETCHING',
  DONE = 'DONE',
}

/**
 * Custom hook that checks whether to display the welcome modal based on certain conditions.
 * The hook is designed to handle cases where the UI does not show the correct state immediately after a user purchases a product.
 * It is possible that after a user purchases a product, the entitlements and products are not updated immediately and this results
 * into the UI not showing the correct state. This hook was created to help improve these cases.
 *
 * @param benefitsListPageType - The type of benefits list page.
 */
export const useCheckShouldDisplayWelcomeModal = (benefitsPage: BenefitsListPage) => {
  const { nestedPath } = useNavigationPaths();
  const [refetchStatus, setRefetchStatus] = React.useState<RefetchStatus>(RefetchStatus.DISABLED);
  const [searchParams] = useSearchParams();
  const hasSuccessSearchParam = searchParams.get('success') === 'true';
  const { initializing: areProductsLoading } = useProducts();
  const hasMieterPlus = usePlusRentEntitlement();
  const hasWohnenPlus = useLivingBundleEntitlement();
  // Max time to refetch is 6 seconds
  const maxRefetchTime = 6000;
  // Refetch interval value is 2 seconds
  const refetchInterval = 2000;

  const shouldRefetchProductsAndEntitlements = React.useMemo(
    () =>
      getShouldRefetchProductsAndEntitlements({
        hasMieterPlus: !!hasMieterPlus,
        hasWohnenPlus: !!hasWohnenPlus,
        areProductsLoading,
        benefitsPage,
        nestedPath,
        hasSuccessSearchParam,
      }),
    [hasMieterPlus, hasWohnenPlus, areProductsLoading, hasSuccessSearchParam, nestedPath, benefitsPage],
  );

  React.useEffect(() => {
    // If shouldRefetchProductsAndEntitlements is true and refetchStatus has a value of DISABLED,
    // then we initiate the refetching process by setting the refetchStatus to REFETCHING
    if (shouldRefetchProductsAndEntitlements && refetchStatus === RefetchStatus.DISABLED) {
      setRefetchStatus(RefetchStatus.REFETCHING);
    }
  }, [shouldRefetchProductsAndEntitlements, refetchStatus]);

  React.useEffect(() => {
    if (refetchStatus === RefetchStatus.DISABLED) return;

    const endRefetching = () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      setRefetchStatus(RefetchStatus.DONE);
    };

    const intervalId = setInterval(() => {
      if (refetchStatus === RefetchStatus.DONE || !shouldRefetchProductsAndEntitlements) {
        endRefetching();
      }
      if (refetchStatus === RefetchStatus.REFETCHING && shouldRefetchProductsAndEntitlements) {
        queryClient.invalidateQueries(['products']);
        queryClient.invalidateQueries(['entitlements']);
      }
    }, refetchInterval);

    // Clear the timer after 6 seconds
    const timeoutId = setTimeout(() => {
      endRefetching();
    }, maxRefetchTime);

    return () => {
      endRefetching();
    };
  }, [refetchStatus, shouldRefetchProductsAndEntitlements]);
};
